<template>
  <div class="share" :class="{ 'share--mini': !isClassic }">
    <modal :show-background="isClassic" @close="$emit('close')">
      <template #header>
        <h2 class="share__heading">Share</h2>
      </template>
      <template #body>
        <div :class="isClassic ? 'inline-share inline-share-classic' : 'inline-share'">
          <div>
            <a
              :href="computedShareLink('facebook')"
              target="_blank"
              :class="isClassic ? 'share-email share-email-classic' : 'share-email'"
            >
              <img :src="icons.facebook" alt="facebook">
              <span>Facebook</span>
            </a>
          </div>
          <div>
            <a
              :href="computedShareLink('twitter')"
              target="_blank"
              :class="isClassic ? 'share-email share-email-classic' : 'share-email'"
            >
              <img :src="icons.twitter" alt="twitter">
              <span>Twitter</span>
            </a>
          </div>
          <div>
            <button
              @click="copyLink"
              :class="isClassic ? 'share-email share-email-classic share-email-button' : 'share-email share-email-button'"
            >
              <img :src="icons.copy" alt="facebook">
              <span>Copy</span>
            </button>
          </div>
          <div>
            <a
              :href="computedShareLink('email')"
              target="_blank"
              :class="isClassic ? 'share-email share-email-classic' : 'share-email'"
            >
              <img :src="emailIcon" alt="email">
              <span>Email</span>
            </a>
          </div>
        </div>
      </template>
    
    </modal>
  </div>
</template>

<script>
import Modal from './Utils/Modal';
import { buildShareLink } from '../utils/share';
import { copyToClipboard, NOTHING_TO_COPY } from '../utils/clipboard';
import { reportNotAllowedIfApplicable } from '../core/faro';
// Vue1 `require('...svg')` -> ES-module imports so Vite resolves the assets
// (inlined as data URIs under the build's assetsInlineLimit). Sharing goes
// through the direct-link builder (utils/share.js) only — no third-party
// forwarder or injected script (kept per the P1-8/MR-12 removal).
import emailSvg from '../assets/v3-assets/email.svg';
import twitterSvg from '../assets/v3-assets/twit.svg';
import facebookSvg from '../assets/v3-assets/fb.svg';
import copySvg from '../assets/v3-assets/copy.svg';

export default {
  components: {
    Modal,
  },
  props: {
    isClassic: Boolean,
    url: {
      type: String,
    },
    title: {
      type: String,
      default: 'default',
    },
    description: {
      type: String,
      default: 'default',
    },
  },
  computed: {
    emailIcon() {
      return emailSvg;
    },
    icons() {
      // The Email button reads `emailIcon` (below), not icons.email — so this
      // map only carries the three it renders (facebook/twitter/copy).
      return {
        twitter: twitterSvg,
        facebook: facebookSvg,
        copy: copySvg,
      };
    },
  },
  methods: {
    copyLink(event) {
      event.preventDefault();
      // #856: async Clipboard API with a synchronous execCommand fallback for
      // the embedded-iframe / http:// cases where writeText is denied/absent.
      copyToClipboard(this.url)
        .then(() => window.alert('Successfully copied link to clipboard.'))
        // Same distinction as v4's Menu: no URL is our data, not their browser.
        .catch((error) => {
          if (error && error.code === NOTHING_TO_COPY) return;
          window.alert('Failed to copy to clipboard. Please check your browser\'s clipboard settings');
          // 🔴 D-146's clipboard half, which reached v4's Menu and not this file. The two
          // catches are near identical, so a listener whose browser denies the clipboard
          // saw the same alert on both versions while the operator got a measurement from
          // only one. `reportNotAllowedIfApplicable` filters to NotAllowedError itself and
          // never throws, so it is safe on the benign paths above it.
          //
          // ⚠️ NO STATION, and that is a stated limitation rather than an oversight. This
          // component's props are `isClassic / url / title / description` — no podcast and
          // no station — and its only render site (PlayerInfo) does not have one either.
          // Threading it would touch v3 Player -> PlayerInfo -> Share, which is a prop
          // plumbing change beyond this fix. The measurement still CLASSIFIES the denial,
          // which is what was missing; it simply cannot attribute it to a station yet.
          reportNotAllowedIfApplicable(error);
        });
    },
    computedShareLink(type) {
      // Delegates to the tested share-URL builder (direct links, no
      // third-party forwarder or injected share script). Template calls
      // facebook / twitter / email.
      return buildShareLink(type, { url: this.url, title: this.title });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../assets/variables-v2';

.share {
  &--mini {
    .share__heading {
      margin-bottom: 0.5rem;
    }
  }
  &__heading {
    font-family: $font;
    font-size: 1.25rem;
    margin-bottom: 1rem;
  }
}
</style>

<style lang="scss">
.share {
  &--mini {
    .modal__content {
      padding: 0.25rem 1rem !important;
    }

    .modal__button {
      top: 0.5rem !important;
    }

    .share__heading {
      margin-bottom: 0 !important;
      font-size: 1rem;
    }
  }
}

.modal {
  &__content {
    padding: 0.5rem 1rem !important;
  }
  &__button {
    top: 1rem !important;
    right: 1rem !important;
  }
}

.inline-share {
  display: flex;
  flex-direction: row;
  justify-content: space-around;
}

.share-email {
  display: flex;
  flex-direction: column;
  text-decoration: none;
  align-items: center;
  margin-top: 6px;
  img {
    height: 20px;
    width: 20px;
  }

  span {
    font-size: 11.4px;
    text-decoration: none;
    color: #000;
    font-family: "Helvetica Neue",Helvetica,Arial,sans-serif;
  }
}

.share-email-classic {
  margin-top: 0px;
  span {
    font-family: "Helvetica Neue",Helvetica,Arial,sans-serif;
    margin-top: 10px;
  }
  img {
    height: 40px;
    width: 40px;
  }
}

.share-email-button {
  background: none;
  border: none;

  &:hover {
    cursor: pointer;
  }
}
</style>
